import { useEffect, useRef } from 'react';

import { LoginButton } from '@components/container/loginButton';
import ArrowButton from '@components/shared/arrowButton';
import FavoritterWrapper from '@components/shared/favoritter/favoritter-wrapper';
import VerktoyMetaInfoMineVerktoy from '@components/verktoy-details/components/metadata/verktoy-metainfo-mine-verktoy';
import { useGetResources } from '@hooks/useGetResources';
import { FavoritterContext } from '@src/types/FavoritterContext';
import { LenkeType, type Lenke, type MittVerktoyItem, VerktoyType } from '@src/types/minhelseentities';
import { PageRoutes, SessionStorageKeys } from '@src/types/types';
import { useAppDispatch, useAppSelector, type VerktoyRootState } from '@store';
import { setStartAktivitet, showRemoveAktivitetConfirmationMineVerktoy } from '@store/verktoy/slice/verktoySlice';
import { fetchMineVerktoyV2Thunk } from '@store/verktoy/thunks';
import { sporAapningAvVerktoy } from '@utils/amplitude-utils';
import { launchAktivitet } from '@utils/launch-aktivitet';
import { hentSokParam } from '@utils/queryParamUtil';
import { getDeleteButtonTextMineVerktoy, trackAnalyticsBasertPaaLenkeType } from '@utils/utils';
import { useLocation, useNavigate } from 'react-router';

import type { StartVerktoyParams } from '@src/types/startVerktoyParams';

import EmptyState from '@helsenorge/designsystem-react/components/EmptyState';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import ArrowRight from '@helsenorge/designsystem-react/components/Icons/ArrowRight';
import TrashCan from '@helsenorge/designsystem-react/components/Icons/TrashCan';
import LinkList from '@helsenorge/designsystem-react/components/LinkList';
import Loader from '@helsenorge/designsystem-react/components/Loader';
import Panel from '@helsenorge/designsystem-react/components/Panel';
import PanelList from '@helsenorge/designsystem-react/components/PanelList';
import PopMenu, { PopMenuLabelPosition } from '@helsenorge/designsystem-react/components/PopMenu';
import Title from '@helsenorge/designsystem-react/components/Title';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

import styles from './styles/mine-verktoy-preview.module.scss';
import { VerktoyLogo } from './verktoy-logo';
import VerktoyActions from './VerktoyActions';

const VERKTOY_LOGO_WIDTH = 48;

export const MineVerktoyList = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const resources = useGetResources();
  const laeringsaktiviteter = useAppSelector((globalState: VerktoyRootState) => globalState.verktoy.mineVerktoyListe);
  const laeringsaktiviteterLoading = useAppSelector((globalState: VerktoyRootState) => globalState.verktoy.laeringsaktiviteterLoading);
  const isLoggedIn: boolean = isAuthorized();
  const navigate = useNavigate();
  const location = useLocation();
  const openVerktoyButtonRef = useRef<HTMLButtonElement>(null);

  const startAktivitet = (laeringsaktivtetId: number): void => {
    dispatch(setStartAktivitet({ aktivitetId: laeringsaktivtetId }));
  };

  const startVerktoy = (params: StartVerktoyParams): void => {
    startAktivitet(params.laeringsaktivtetId);
    trackAnalyticsBasertPaaLenkeType(params.lenkeType, params.verktoyNavn);
    sporAapningAvVerktoy(params.verktoyNavn, params.lenkeType, params.interntVerktoy);
    launchAktivitet({
      verktoyId: params.verktoyId,
      oppstartsType: params.oppstartsType,
      lenker: params.lenker,
      lenkeType: params.lenkeType,
      kanStartesAvInnloggetBruker: params.kanStartesAvInnloggetBruker,
    });
  };

  const visVerktoyDetaljer = (verktoyId: string): void => {
    sessionStorage.setItem(SessionStorageKeys.returnLocation, PageRoutes.finnVerktoy + '/' + verktoyId + '?' + hentSokParam(resources));
    navigate(`/${verktoyId}`, { state: { from: PageRoutes.entryPage } });
  };

  const goToFinnVerktoy = (): void => {
    navigate(PageRoutes.finnVerktoy, { state: { from: PageRoutes.entryPage } });
  };

  const fetchLaeringsaktiviteterHandler = (): void => {
    if (isLoggedIn) {
      dispatch(fetchMineVerktoyV2Thunk());
    }
  };

  const showDeleteMineVerktoy = (MittVerktoyItem: MittVerktoyItem): void => {
    dispatch(showRemoveAktivitetConfirmationMineVerktoy({ MittVerktoyItem }));
  };

  useEffect(() => {
    fetchLaeringsaktiviteterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.pathname === PageRoutes.entryPage) {
      fetchLaeringsaktiviteterHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const skalViseAapneVerktoyKnapp = (lenker: Lenke[], verktoyTyper: VerktoyType[], kanStartesAvInnloggetBruker: boolean): boolean => {
    return (
      Array.isArray(verktoyTyper) &&
      verktoyTyper.includes(VerktoyType.Weblosning) &&
      lenker.some(lenke => lenke.LenkeType === LenkeType.Standard) &&
      kanStartesAvInnloggetBruker
    );
  };

  return (
    <div>
      <ArrowButton
        className={styles.seAlleSelvhjelpsverktoyBtn}
        text={resources?.se_alle_selvhjelpsverktoy_btn_text || ''}
        onClick={() => {
          goToFinnVerktoy();
        }}
        variant="outline"
        arrow="icon"
      />

      <div className={styles.titleMineVerktoy}>
        <Title appearance="title2" htmlMarkup="h2">
          {resources?.mineVerktoy_header}
        </Title>
      </div>

      {!isLoggedIn && (
        <>
          <div className={`${styles.elementMarginTop} ${styles.elementMarginBottom}`}>
            <span>{resources?.entryPage_loginInfo}</span>
          </div>
          <LoginButton />
        </>
      )}

      {isLoggedIn && (
        <>
          {laeringsaktiviteterLoading && <Loader testId="preview-loader" />}
          {laeringsaktiviteter?.length === 0 && (
            <div className={styles.elementMarginTop}>
              {!laeringsaktiviteterLoading && <EmptyState title={resources?.entryPage_emptyState} />}
            </div>
          )}
          {!laeringsaktiviteterLoading && laeringsaktiviteter?.length > 0 && (
            <PanelList>
              {laeringsaktiviteter.map((verktoy: MittVerktoyItem) => {
                const visAapneVerktoy =
                  skalViseAapneVerktoyKnapp(verktoy.Lenker, verktoy.VerktoyType, verktoy.KanStartesAvInnloggetBruker) ||
                  !verktoy.ErVerktoyTilgjengelig;

                return (
                  <Panel
                    key={verktoy.VerktoyId + verktoy.LagtTilDato}
                    color="neutral"
                    testId={`panel-${verktoy.VerktoyId}-${verktoy.VerktoyNavn}`}
                  >
                    <Panel.A>
                      <div className={styles.panelPreContainerFlex}>
                        <VerktoyLogo width={VERKTOY_LOGO_WIDTH} logoId={verktoy.GrafiskElementId} />
                        <PopMenu
                          openButtonTestId="administrer-varsler-button"
                          labelText={resources?.mineverktoy_popmenu_label}
                          labelTextPosition={PopMenuLabelPosition.left}
                        >
                          <LinkList>
                            {visAapneVerktoy && (
                              <LinkList.Link onClick={() => visVerktoyDetaljer(verktoy.VerktoyId)} icon={<Icon svgIcon={ArrowRight} />}>
                                {resources?.mineverktoy_popmenu_vis_mer_om_verktoy_btn_label}
                              </LinkList.Link>
                            )}

                            <LinkList.Link
                              htmlMarkup="button"
                              onClick={() => {
                                showDeleteMineVerktoy(verktoy);
                              }}
                              testId={`delete-verktoy-mobile-${verktoy.VerktoyId}`}
                              icon={<Icon svgIcon={TrashCan} />}
                            >
                              {getDeleteButtonTextMineVerktoy(verktoy, resources)}
                            </LinkList.Link>
                          </LinkList>
                        </PopMenu>
                      </div>
                    </Panel.A>

                    <Panel.B>
                      {/* <Panel.Title
                        title={verktoy.VerktoyNavn}
                        badge={<FavoritterWrapper context={FavoritterContext.MineVerktoy} item={verktoy} />}
                      /> */}
                      <div className={styles.titleFavoriteWrapper}>
                        <h3 className={styles.title}>{verktoy.VerktoyNavn}</h3>
                        <span className={styles.favorite}>
                          <FavoritterWrapper context={FavoritterContext.MineVerktoy} item={verktoy} />
                        </span>
                      </div>
                    </Panel.B>
                    <Panel.C>
                      <VerktoyMetaInfoMineVerktoy mittVerktoy={verktoy} />
                      <VerktoyActions
                        verktoy={verktoy}
                        showOpenVerktoy={visAapneVerktoy}
                        openVerktoyButtonRef={openVerktoyButtonRef as React.RefObject<HTMLButtonElement>}
                        startVerktoy={startVerktoy}
                        visVerktoyDetaljer={visVerktoyDetaljer}
                        resources={resources}
                      />
                    </Panel.C>
                  </Panel>
                );
              })}
            </PanelList>
          )}
        </>
      )}
    </div>
  );
};
